/**
 * CanvasAutoSave — effect-based bridge between dirty tracker and save queue.
 *
 * Watches state.dirtyTracker.canvasPages (populated by commands internally)
 * and enqueues saves with the settled (post-dispatch) state, avoiding the
 * stale-closure bug that inline save calls suffer from.
 *
 * Renders nothing — this is a pure side-effect component.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useUIModelState } from '../contexts/UIModelStateContext';
import { useSaveQueue } from '../contexts/SaveQueueContext';
import { buildCanvasSaveData } from '../utils/canvasSaveHelper';

const CanvasAutoSave = () => {
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();

  // Use ref to always have latest state without adding it as effect dependency
  const stateRef = useRef(state);
  stateRef.current = state;

  // Stable string key derived from the Set — only changes when actual IDs change
  const dirtyCanvasKey = useMemo(
    () => Array.from(state.dirtyTracker.canvasPages).sort().join(','),
    [state.dirtyTracker.canvasPages]
  );

  useEffect(() => {
    if (!dirtyCanvasKey) return; // no dirty canvases

    const canvasIds = dirtyCanvasKey.split(',');
    const currentState = stateRef.current;

    for (const canvasId of canvasIds) {
      const saveData = buildCanvasSaveData(currentState, canvasId);
      if (saveData) {
        enqueueSave('canvas', saveData, canvasId);
        // Only clear dirty flag for canvases that successfully built save data
        dispatch({ type: 'MARK_CLEAN', payload: { section: 'canvas', canvasId } });
      } else {
        // Don't clear dirty flag — let a future render retry the save
        console.warn(`[CanvasAutoSave] buildCanvasSaveData returned null for canvas "${canvasId}". Dirty flag retained for retry.`);
      }
    }
  }, [dirtyCanvasKey, dispatch, enqueueSave]);

  return null;
};

export default CanvasAutoSave;
